import axios, { AxiosResponse } from "axios";

import CustomHttpClient from "../customHttpClient";

import { getError } from "../../utils/getError";
import cryptoJS from "../../utils/cryptoJS";
import userHelper from "../../utils/userHelper";

import { Intermediaries, Token, UserInformation } from "../../interfaces";
import {
  AuthenticationDetails,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { poolData } from "../../../awsConfig";
import localStorageHelper from "../../utils/localStorageHelper";

const customHttpClientProducers = CustomHttpClient({ baseUrl: "productores" });

const { REACT_APP_LAMBDA_GET_TOKEN_URL, REACT_APP_API_URL, REACT_APP_CLIENT_ID } =
  process.env;

const userPool = new CognitoUserPool(poolData);

const AuthenticationService = {
  async getToken(scope: string): Promise<AxiosResponse<Token>> {
    const url = REACT_APP_LAMBDA_GET_TOKEN_URL;

    const accessToken = localStorageHelper.get<string>("access_token");

    const headers = {
      Authorization: `Bearer ${accessToken}`,
    };

    try {
      const response = await axios.post(
        `${url}/web_productores_get_token`,
        { scope },
        { headers }
      );
      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async login(
    user: string,
    pass: string
  ): Promise<{ idToken: string; accessToken: string; refreshToken: string }> {
    const region = poolData.UserPoolId.split('_')[0];
    const cognitoUrl = `https://cognito-idp.${region}.amazonaws.com/`;
    
    const authParams = {
      AuthFlow: "USER_PASSWORD_AUTH",
      ClientId: poolData.ClientId,
      AuthParameters: {
        USERNAME: user,
        PASSWORD: pass,
      },
    };

    try {
      const response = await axios.post(cognitoUrl, authParams, {
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
        },
      });

      const { AuthenticationResult } = response.data;
      
      return {
        idToken: AuthenticationResult.IdToken,
        accessToken: AuthenticationResult.AccessToken,
        refreshToken: AuthenticationResult.RefreshToken,
      };
    } catch (error) {
      throw error;
    }
  },

  cognitoLogout() {
    const cognitoUser = userPool.getCurrentUser();

    if (cognitoUser) {
      cognitoUser.signOut();
    }
  },

  async refreshSession(refreshToken: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();

      if (!cognitoUser) return reject("No hay usuario actual");

      cognitoUser.refreshSession(
        new CognitoRefreshToken({ RefreshToken: refreshToken }),
        (err, session) => {
          if (err) return reject(err);
          resolve({
            idToken: session.getIdToken().getJwtToken(),
            accessToken: session.getAccessToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken(),
          });
        }
      );
    });
  },

  async getUserInformation(
    user: string,
    accessToken: string
  ): Promise<AxiosResponse<UserInformation>> {
    const url = `${REACT_APP_API_URL}productores/obtenerDatosDeUsuario?uid=${user}`;
    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          scope: "Productores",
          "x-ibm-client-id": REACT_APP_CLIENT_ID,
        },
      });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getIntermediariesByUser(): Promise<AxiosResponse<Intermediaries>> {
    const url = "intermediariosPorCuit";

    try {
      const user = userHelper.getUser();
      const response = await customHttpClientProducers.get(url, {
        params: {
          empresa: user?.codigoDeEmpresa,
          sucursal: user?.codigoDeSucursal,
          cuit: user?.cuit,
          usri: user?.usuarioInterno,
        },
      });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getIntermediariesByIntermediary(): Promise<
    AxiosResponse<Intermediaries>
  > {
    const url = "intermediariosPorIntermediario";

    try {
      const user = userHelper.getUser();
      const response = await customHttpClientProducers.get(url, {
        params: {
          empresa: user?.codigoDeEmpresa,
          sucursal: user?.codigoDeSucursal,
          nivt: user?.intermediarioSecundario?.nivelInter,
          nivc: user?.intermediarioSecundario?.codigoInter,
        },
      });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    const region = poolData.UserPoolId.split('_')[0];
    const url = `https://cognito-idp.${region}.amazonaws.com/`;
    const accessToken = localStorageHelper.get<string>("access_token");

    const body = {
      PreviousPassword: oldPassword,
      ProposedPassword: newPassword,
      AccessToken: accessToken
    };

    const headers = {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.ChangePassword',
    };

    try {
      await axios.post(url, body, { headers });
    } catch (err) {
      throw err;
    }
  },

  async forgotPassword(username: string) {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool
      });
  
      cognitoUser.forgotPassword({
        onSuccess: (data) => {
          // Código enviado
          resolve(data);
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  },

  async confirmForgotPassword(username: string, verificationCode: string, newPassword: string) {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool
      });
  
      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => {
          resolve(true);
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  }
};

export default AuthenticationService;
